import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { AppCard } from '../ui/AppCard';
import { SegmentedControl } from '../ui/SegmentedControl';

// Per-OS "run the agent in the background + check it" help for the Agents
// page. Mirrors `zagentc help service` / `zagentc help status` (zagentc/src/
// help.c) - keep the two in step. Windows has no daemon (POSIX-only), so it
// runs the one-shot importer from Task Scheduler.
type Os = 'linux' | 'macos' | 'windows';

// Shown above the per-OS steps: one login, reused by every command.
const LOGIN_STEP: Step = {
  label: 'Log in once on the machine (no password is stored; the session is saved to ~/.zmistrc)',
  code: 'zagentc login\nzagentc whoami          # who am I, and until when\nzagentc logout          # revoke the session and delete the file',
};

interface Step {
  label: string;
  code: string;
}

const HOST = '--host api.zmist.co --https --api-port 443';

const GUIDE: Record<Os, { intro: string; steps: Step[]; check: Step[] }> = {
  linux: {
    intro: 'Runs the daemon under systemd: one connection, every integration on its own schedule, restarted if it dies.',
    steps: [
      {
        label: '/etc/systemd/system/zagentc.service',
        code: `[Unit]
Description=Zmist agent
After=network-online.target
Wants=network-online.target

[Service]
User=zagentc
Environment=HOME=/var/lib/zagentc
Environment=CUE_AGENT_NAME=my-server
ExecStart=/usr/local/bin/zagentc daemon --import --email you@example.com --password '...' ${HOST}
Restart=always
RestartSec=5

[Install]
WantedBy=multi-user.target`,
      },
      { label: 'Enable and start', code: 'sudo systemctl daemon-reload && sudo systemctl enable --now zagentc' },
    ],
    check: [
      { label: 'Is the service up?', code: 'systemctl status zagentc' },
      { label: 'Live log', code: 'journalctl -u zagentc -f' },
      { label: "The daemon's own answer", code: 'sudo -u zagentc HOME=/var/lib/zagentc zagentc status' },
      { label: 'Update', code: 'sudo systemctl restart zagentc   # after replacing the binary' },
    ],
  },
  macos: {
    intro: 'Runs the daemon as a launchd agent that starts at login and is restarted if it exits.',
    steps: [
      {
        label: '~/Library/LaunchAgents/co.zmist.zagentc.plist',
        code: `<?xml version="1.0" encoding="UTF-8"?>
<plist version="1.0"><dict>
  <key>Label</key><string>co.zmist.zagentc</string>
  <key>ProgramArguments</key><array>
    <string>/usr/local/bin/zagentc</string><string>daemon</string><string>--import</string>
    <string>--email</string><string>you@example.com</string>
    <string>--password</string><string>...</string>
    <string>--host</string><string>api.zmist.co</string><string>--https</string>
    <string>--api-port</string><string>443</string>
  </array>
  <key>RunAtLoad</key><true/><key>KeepAlive</key><true/>
  <key>StandardErrorPath</key><string>/tmp/zagentc.log</string>
</dict></plist>`,
      },
      { label: 'Load it', code: 'launchctl load ~/Library/LaunchAgents/co.zmist.zagentc.plist' },
    ],
    check: [
      { label: 'Is it running? (a PID in the first column)', code: 'launchctl list | grep zagentc' },
      { label: 'Live log', code: 'tail -f /tmp/zagentc.log' },
      { label: "The daemon's own answer", code: 'zagentc status' },
      { label: 'Update', code: 'launchctl kickstart -k gui/$(id -u)/co.zmist.zagentc   # after replacing the binary' },
    ],
  },
  windows: {
    intro:
      'The background daemon is not available on Windows. Run the one-shot importer from Task Scheduler every few minutes instead - each run imports every integration once, and anything the backend refuses is retried on the next run.',
    steps: [
      {
        label: 'Create the scheduled task',
        code: `schtasks /Create /TN zagentc /SC MINUTE /MO 5 /RU SYSTEM ^
  /TR "C:\\zagentc\\zagentc.exe import --email you@example.com --password ... ${HOST}"`,
      },
    ],
    check: [
      { label: 'Last run time and result (0 = ok)', code: 'schtasks /Query /TN zagentc /V /FO LIST' },
      { label: 'Run it now', code: 'schtasks /Run /TN zagentc' },
      { label: 'Remove it', code: 'schtasks /Delete /TN zagentc /F' },
      { label: 'Update', code: 'Replace zagentc.exe - the next scheduled run uses it.' },
    ],
  },
};

function detectOs(): Os {
  const p = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return /Windows/i.test(p) ? 'windows' : /Mac/i.test(p) ? 'macos' : 'linux';
}

function CodeBlock({ step }: { step: Step }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(step.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked - the text is still selectable */
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-black/50 dark:text-white/50">{step.label}</span>
      <div className="relative rounded-lg bg-black/[0.06] dark:bg-white/10">
        <pre className="overflow-x-auto p-3 pr-10 text-[11px] leading-relaxed font-mono whitespace-pre">{step.code}</pre>
        <button
          onClick={copy}
          aria-label={`Copy: ${step.label}`}
          className="absolute right-2 top-2 rounded-md p-1 text-black/40 hover:text-current dark:text-white/40"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

export function AgentSetupHelp() {
  const [os, setOs] = useState<Os>(detectOs);
  const g = GUIDE[os];
  return (
    <AppCard className="flex flex-col gap-3">
      <details>
        <summary className="cursor-pointer font-semibold text-sm">Run an agent in the background and check its status</summary>
        <div className="mt-3 flex flex-col gap-3">
          <SegmentedControl<Os>
            value={os}
            onChange={setOs}
            options={[
              { value: 'linux', label: 'Linux' },
              { value: 'macos', label: 'macOS' },
              { value: 'windows', label: 'Windows' },
            ]}
          />
          <p className="text-xs text-black/60 dark:text-white/60">{g.intro}</p>
          <CodeBlock step={LOGIN_STEP} />
          {g.steps.map((s) => (
            <CodeBlock key={s.label} step={s} />
          ))}
          <p className="text-sm font-medium">Check that it is running</p>
          {g.check.map((s) => (
            <CodeBlock key={s.label} step={s} />
          ))}
          <p className="text-xs text-black/50 dark:text-white/50">
            Once an agent has completed a run it appears below with its online state, version and backlog. The same steps are in{' '}
            <code className="font-mono">zagentc help service</code> and <code className="font-mono">man zagentc</code>.
          </p>
        </div>
      </details>
    </AppCard>
  );
}
